import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Plus, Trash2, Edit2, LogOut, Download } from 'lucide-react';
import {
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  setDoc,
  getDoc
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import { auth, db, storage } from './firebase';
import Login from './Login';
import './App.css';

function Home() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [projects, setProjects] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [resumeUrl, setResumeUrl] = useState('');
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showExperienceModal, setShowExperienceModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingExperience, setEditingExperience] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Form states
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    image: '',
    link: ''
  });

  const [experienceForm, setExperienceForm] = useState({
    title: '',
    company: '',
    date: '',
    description: ''
  });

  // Check Auth State
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAdmin(!!user);
    });
    return () => unsubscribe();
  }, []);

  // Load data from Firestore
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load Projects
      const projectsQuery = query(collection(db, 'projects'), orderBy('createdAt', 'desc'));
      const projectsSnapshot = await getDocs(projectsQuery);
      const projectsData = projectsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProjects(projectsData);

      // Load Experiences
      const experiencesQuery = query(collection(db, 'experiences'), orderBy('createdAt', 'desc'));
      const experiencesSnapshot = await getDocs(experiencesQuery);
      const experiencesData = experiencesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setExperiences(experiencesData);

      // Load Resume URL (stored in a 'settings' collection or specific doc)
      const resumeDoc = await getDoc(doc(db, 'settings', 'resume'));
      if (resumeDoc.exists()) {
        setResumeUrl(resumeDoc.data().url);
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout error", error);
    }
  };

  const uploadImage = async (file, path) => {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleImageUpload = async (e, isProject = true) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) {
        alert('File size should be less than 5MB');
        return;
      }

      try {
        const path = `images/${Date.now()}_${file.name}`;
        const url = await uploadImage(file, path);

        if (isProject) {
          setProjectForm({ ...projectForm, image: url });
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image');
      }
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) {
        alert('File size should be less than 5MB');
        return;
      }

      try {
        const path = `resumes/${Date.now()}_${file.name}`;
        const url = await uploadImage(file, path);

        // Save URL to Firestore
        await setDoc(doc(db, 'settings', 'resume'), { url });
        setResumeUrl(url);
        alert('Resume uploaded successfully!');
      } catch (error) {
        console.error('Error uploading resume:', error);
        alert('Failed to upload resume');
      }
    }
  };

  const saveProject = async () => {
    if (!projectForm.name || !projectForm.description || !projectForm.image || !projectForm.link) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const projectData = {
        ...projectForm,
        createdAt: editingProject ? editingProject.createdAt : Date.now()
      };

      if (editingProject) {
        await updateDoc(doc(db, 'projects', editingProject.id), projectData);
        setProjects(projects.map(p => p.id === editingProject.id ? { ...projectData, id: p.id } : p));
      } else {
        const docRef = await addDoc(collection(db, 'projects'), projectData);
        setProjects([{ ...projectData, id: docRef.id }, ...projects]);
      }

      setShowProjectModal(false);
      setProjectForm({ name: '', description: '', image: '', link: '' });
      setEditingProject(null);
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Failed to save project');
    }
  };

  const deleteProject = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await deleteDoc(doc(db, 'projects', id));
        setProjects(projects.filter(p => p.id !== id));
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Failed to delete project');
      }
    }
  };

  const saveExperience = async () => {
    if (!experienceForm.title || !experienceForm.company || !experienceForm.date || !experienceForm.description) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const experienceData = {
        ...experienceForm,
        createdAt: editingExperience ? editingExperience.createdAt : Date.now()
      };

      if (editingExperience) {
        await updateDoc(doc(db, 'experiences', editingExperience.id), experienceData);
        setExperiences(experiences.map(e => e.id === editingExperience.id ? { ...experienceData, id: e.id } : e));
      } else {
        const docRef = await addDoc(collection(db, 'experiences'), experienceData);
        setExperiences([{ ...experienceData, id: docRef.id }, ...experiences]);
      }

      setShowExperienceModal(false);
      setExperienceForm({ title: '', company: '', date: '', description: '' });
      setEditingExperience(null);
    } catch (error) {
      console.error('Error saving experience:', error);
      alert('Failed to save experience');
    }
  };

  const deleteExperience = async (id) => {
    if (window.confirm('Are you sure you want to delete this experience?')) {
      try {
        await deleteDoc(doc(db, 'experiences', id));
        setExperiences(experiences.filter(e => e.id !== id));
      } catch (error) {
        console.error('Error deleting experience:', error);
        alert('Failed to delete experience');
      }
    }
  };

  const editProject = (project) => {
    setEditingProject(project);
    setProjectForm(project);
    setShowProjectModal(true);
  };

  const editExperience = (experience) => {
    setEditingExperience(experience);
    setExperienceForm(experience);
    setShowExperienceModal(true);
  };

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="app">
      {/* Background Animation */}
      <div className="bg-animation">
        <div className="floating-shape shape1"></div>
        <div className="floating-shape shape2"></div>
        <div className="floating-shape shape3"></div>
      </div>

      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">🤖 TidelxD</div>

          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            ☰
          </button>

          <ul className={`nav-links ${mobileMenuOpen ? 'active' : ''}`}>
            <li><a href="#home" onClick={() => setMobileMenuOpen(false)}>Home</a></li>
            <li><a href="#about" onClick={() => setMobileMenuOpen(false)}>About</a></li>
            <li><a href="#projects" onClick={() => setMobileMenuOpen(false)}>Projects</a></li>
            <li><a href="#experience" onClick={() => setMobileMenuOpen(false)}>Experience</a></li>
            <li><a href="#contact" onClick={() => setMobileMenuOpen(false)}>Contact</a></li>
            {isAdmin && (
              <li>
                <button onClick={handleLogout} className="logout-btn">
                  <LogOut size={18} /> Logout
                </button>
              </li>
            )}
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="container">
          <h1>Hi, I'm <span className="highlight">Hanafi Med Riadh</span></h1>
          <p className="subtitle">Android Developer | Building Amazing Mobile Experiences</p>
          <div className="cta-buttons">
            <a href="#projects" className="btn btn-primary">View My Work</a>
            <a href="#contact" className="btn btn-secondary">Get in Touch</a>
            {resumeUrl && (
              <a href={resumeUrl} target="_blank" rel="noopener noreferrer" className="btn btn-resume">
                <Download size={20} /> Download Resume
              </a>
            )}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section">
        <div className="container">
          <h2 className="section-title">About Me</h2>
          <div className="about-content">
            <div className="about-text-container">
              <p className="about-text">
                I'm a passionate Android developer with expertise in building scalable and user-friendly mobile applications.
                With a strong foundation in Kotlin, Java, and modern Android development practices, I create apps that users love.
              </p>
              <p className="about-text">
                I'm constantly learning and staying up-to-date with the latest Android technologies and best practices
                to deliver cutting-edge solutions.
              </p>
            </div>
            <div className="skills">
              {['Kotlin', 'Java', 'Android Studio', 'Jetpack Compose', 'MVVM', 'Room Database', 'Retrofit', 'Firebase', 'Git', 'Material Design'].map(skill => (
                <span key={skill} className="skill-tag">{skill}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">My Projects</h2>
            {isAdmin && (
              <button
                onClick={() => {
                  setEditingProject(null);
                  setProjectForm({ name: '', description: '', image: '', link: '' });
                  setShowProjectModal(true);
                }}
                className="btn btn-add"
              >
                <Plus size={20} /> Add Project
              </button>
            )}
          </div>

          <div className="projects-grid">
            {projects.map(project => (
              <div key={project.id} className="project-card">
                <div className="project-image">
                  {project.image && project.image.startsWith('http') ? (
                    <img src={project.image} alt={project.name} />
                  ) : (
                    <span className="project-emoji">{project.image || '🚀'}</span>
                  )}
                </div>
                <div className="project-content">
                  <h3>{project.name}</h3>
                  <p>{project.description}</p>
                  <div className="project-actions">
                    <a href={project.link} target="_blank" rel="noopener noreferrer" className="project-link">
                      View Project →
                    </a>
                    {isAdmin && (
                      <div className="admin-actions">
                        <button onClick={() => editProject(project)} className="icon-btn edit-btn">
                          <Edit2 size={18} />
                        </button>
                        <button onClick={() => deleteProject(project.id)} className="icon-btn delete-btn">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="section">
        <div className="container-narrow">
          <div className="section-header">
            <h2 className="section-title">Experience</h2>
            {isAdmin && (
              <button
                onClick={() => {
                  setEditingExperience(null);
                  setExperienceForm({ title: '', company: '', date: '', description: '' });
                  setShowExperienceModal(true);
                }}
                className="btn btn-add"
              >
                <Plus size={20} /> Add Experience
              </button>
            )}
          </div>

          <div className="timeline">
            {experiences.map((exp, index) => (
              <div key={exp.id} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <h3>{exp.title}</h3>
                  <div className="company">{exp.company}</div>
                  <div className="date">{exp.date}</div>
                  <p>{exp.description}</p>

                  {isAdmin && (
                    <div className="admin-actions">
                      <button onClick={() => editExperience(exp)} className="icon-btn edit-btn">
                        <Edit2 size={18} />
                      </button>
                      <button onClick={() => deleteExperience(exp.id)} className="icon-btn delete-btn">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Admin Resume Upload Section */}
      {isAdmin && (
        <section className="section">
          <div className="container-narrow">
            <div className="resume-upload-card">
              <h3>Upload Resume</h3>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleResumeUpload}
                className="file-input"
              />
              {resumeUrl && (
                <p className="success-message">✓ Resume uploaded successfully</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section id="contact" className="section">
        <div className="container-narrow">
          <h2 className="section-title">Let's Connect</h2>
          <div className="contact-content">
            <p className="contact-text">
              Interested in working together? Feel free to reach out via WhatsApp or Email!
            </p>
            <div className="contact-buttons">
              <a href="https://wa.me/213559282520" target="_blank" rel="noopener noreferrer" className="btn btn-contact">
                📱 WhatsApp
              </a>
              <a href="https://mail.google.com/mail/?view=cm&fs=1&to=med.riadh.hanafi@gmail.com" target="_blank" rel="noopener noreferrer" className="btn btn-contact">
                ✉️ Email
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p>&copy; 2024 Hanafi Med Riadh. All rights reserved.</p>
      </footer>

      {/* Project Modal */}
      {showProjectModal && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <h2>{editingProject ? 'Edit Project' : 'Add New Project'}</h2>
            <div className="form-group">
              <label>Project Name</label>
              <input
                type="text"
                value={projectForm.name}
                onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                placeholder="My Awesome App"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={projectForm.description}
                onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                placeholder="Brief description of your project..."
                rows="4"
              />
            </div>
            <div className="form-group">
              <label>Project Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="file-input"
              />
              {projectForm.image && (
                <div className="image-preview">
                  {projectForm.image.startsWith('http') ? (
                    <img src={projectForm.image} alt="Preview" />
                  ) : (
                    <div className="emoji-preview">{projectForm.image}</div>
                  )}
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Project Link</label>
              <input
                type="text"
                value={projectForm.link}
                onChange={(e) => setProjectForm({ ...projectForm, link: e.target.value })}
                placeholder="https://github.com/..."
              />
            </div>
            <div className="modal-actions">
              <button onClick={saveProject} className="btn btn-primary">
                {editingProject ? 'Update' : 'Add'} Project
              </button>
              <button
                onClick={() => {
                  setShowProjectModal(false);
                  setEditingProject(null);
                  setProjectForm({ name: '', description: '', image: '', link: '' });
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Experience Modal */}
      {showExperienceModal && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <h2>{editingExperience ? 'Edit Experience' : 'Add New Experience'}</h2>
            <div className="form-group">
              <label>Job Title</label>
              <input
                type="text"
                value={experienceForm.title}
                onChange={(e) => setExperienceForm({ ...experienceForm, title: e.target.value })}
                placeholder="Android Developer"
              />
            </div>
            <div className="form-group">
              <label>Company</label>
              <input
                type="text"
                value={experienceForm.company}
                onChange={(e) => setExperienceForm({ ...experienceForm, company: e.target.value })}
                placeholder="Company Name"
              />
            </div>
            <div className="form-group">
              <label>Date Range</label>
              <input
                type="text"
                value={experienceForm.date}
                onChange={(e) => setExperienceForm({ ...experienceForm, date: e.target.value })}
                placeholder="Jan 2023 - Present"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={experienceForm.description}
                onChange={(e) => setExperienceForm({ ...experienceForm, description: e.target.value })}
                placeholder="Key responsibilities and achievements..."
                rows="4"
              />
            </div>
            <div className="modal-actions">
              <button onClick={saveExperience} className="btn btn-primary">
                {editingExperience ? 'Update' : 'Add'} Experience
              </button>
              <button
                onClick={() => {
                  setShowExperienceModal(false);
                  setEditingExperience(null);
                  setExperienceForm({ title: '', company: '', date: '', description: '' });
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/admin" element={<Login />} />
      </Routes>
    </Router>
  );
}

export default App;